/**
 * Catalogue tab bar — four catalogue tabs with count chips plus Analytics,
 * ported from the LifeScribe original including the pulsing gold dot that marks
 * a catalogue whose ordering failed to autosave.
 */

import React from 'react';
import { Box, Tabs, Tab, Chip } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { colors } from '../../../theme/designTokens';
import { TAB_CONFIGS } from './constants';
import { CatalogueKey } from './types';

export interface AIStylesTabBarProps {
  tabValue: number;
  onChange: (next: number) => void;
  counts: number[];
  /** Catalogues whose order autosave failed — they still need a manual Save. */
  unsavedCatalogues: Set<CatalogueKey>;
}

const AIStylesTabBar: React.FC<AIStylesTabBarProps> = ({
  tabValue,
  onChange,
  counts,
  unsavedCatalogues,
}) => (
  <Box
    sx={{
      bgcolor: colors.deepTeal[500],
      borderTop: '1px solid rgba(255,255,255,0.08)',
      px: 1,
    }}
  >
    <Tabs
      value={tabValue}
      onChange={(_e, next) => onChange(next)}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="Style catalogues"
      TabIndicatorProps={{ style: { backgroundColor: colors.warmGold[500], height: 3 } }}
      sx={{
        minHeight: 44,
        '& .MuiTab-root': {
          fontFamily: '"Montserrat", sans-serif',
          fontWeight: 600,
          fontSize: '0.78rem',
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
          minHeight: 44,
          py: 1,
          px: 2,
          '&.Mui-selected': { color: '#fff' },
        },
      }}
    >
      {TAB_CONFIGS.map((config, index) => (
        <Tab
          key={config.catalogue}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              {config.label}
              <Chip
                size="small"
                label={counts[index] ?? 0}
                sx={{
                  height: 18,
                  minWidth: 24,
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  bgcolor: tabValue === index ? colors.warmGold[500] : 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
              {unsavedCatalogues.has(config.catalogue) && (
                <Box
                  aria-label={`Unsaved order in ${config.label}`}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: colors.warmGold[500],
                    animation: 'quillStylesPulse 1.5s ease-in-out infinite',
                    '@keyframes quillStylesPulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                    },
                  }}
                />
              )}
            </Box>
          }
        />
      ))}
      <Tab icon={<AssessmentIcon sx={{ fontSize: 18 }} />} label="Analytics" iconPosition="start" />
    </Tabs>
  </Box>
);

export default AIStylesTabBar;
